import { Variant } from '../types';
import {
  BundledPotProductAndVariantInfo, CategoryMapRecord, LineItem, MainProductAndVariantInfo,
  PottedProductVariant,
  ProductSubsetForAnalytics, VariantSubsetForAnalytics,
} from './types';
import CartItem from '../component/cart/CartItem';

const CareLevelTags = ['im-easy', 'plays-hard-to-get', 'high-maintenance'];
const WaterNeedTags = ['low-water', 'medium-water', 'high-water'];
const HumidityLevelTags = ['low-humidity', 'medium-humidity', 'high-humidity'];
const LightLevelTags = ['low-light', 'medium-light', 'high-light'];
const PetFriendlinessTags = ['pet-safe', 'pet-warning', 'pet-hazard'];

export const getSizeMetric = (
  plantProduct: ProductSubsetForAnalytics,
  plantVariant: PottedProductVariant | VariantSubsetForAnalytics,
) => {
  const pottedProductVariant = plantVariant as PottedProductVariant;
  const productVariant = plantVariant as Variant;
  const sizeMetric = pottedProductVariant.Size;

  if (sizeMetric) {
    return sizeMetric;
  }

  const sizeOptionIndex = plantProduct.options.findIndex((o) => o === 'Size');

  if (sizeOptionIndex !== -1) {
    return productVariant[`option${sizeOptionIndex + 1}` as keyof Variant];
  }

  return '';
};

export const getColorMetric = (
  product: ProductSubsetForAnalytics,
  variant: PottedProductVariant | VariantSubsetForAnalytics,
  bundledPotVariant?: BundledPotProductAndVariantInfo,
) => {
  const productVariant = variant as Variant;

  const colorOptionIndex = product.options.findIndex((o) => o === 'Color');

  if (colorOptionIndex !== -1) {
    return productVariant[`option${colorOptionIndex + 1}` as keyof Variant];
  }

  return bundledPotVariant?.Color || '';
};

export const zoneComparator = (zoneTagA: string, zoneTagB: string) => {
  try {
    const zoneNumberA = parseInt(zoneTagA.split('zone-')[1], 10);
    const zoneNumberB = parseInt(zoneTagB.split('zone-')[1], 10);

    return zoneNumberA - zoneNumberB;
  } catch (e) {
    return 0;
  }
};

const productCategoriesMap: CategoryMapRecord = (() => {
  let categoryMap = {};

  const categoryMapDataEl: HTMLElement = document.querySelector('[data-product-categories-map]') as HTMLElement;

  if (!categoryMapDataEl) {
    return categoryMap;
  }

  try {
    categoryMap = JSON.parse(categoryMapDataEl.innerText);
  } catch (e) {
    console.error('Error parsing category data');
  }

  return categoryMap;
})();

const findMatchForCategory = (
  categoryMap: CategoryMapRecord,
  productTags: string[],
  categories: string[] = [],
): string[] => {
  if (!categoryMap.childCategories) {
    return categories;
  }

  const categoriesAtLevel = Object.keys(categoryMap.childCategories);

  if (!categoriesAtLevel) {
    return categories;
  }

  const match = categoriesAtLevel.find((category) => productTags.includes(category));

  if (!match) {
    return categories;
  }

  categories.push(match);

  return findMatchForCategory(categoryMap.childCategories[match], productTags, categories);
};

export const parseTagsForProductCategories = (productTags: string[]): Record<string, string> => {
  let categories: string[] = [];

  try {
    categories = findMatchForCategory(productCategoriesMap, productTags);
  } catch (e) {
    console.error('Error parsing pTags categories', e);
  }

  const productCategoryData: Record<string, string> = {};

  categories.forEach((productCategory, index) => {
    productCategoryData[`item_category${index === 0 ? '' : index + 1}`] = productCategory;
  });

  return productCategoryData;
};

const parsePotCustomMetrics = (bundledPotVariant: BundledPotProductAndVariantInfo) => ({
  item_size: bundledPotVariant.Size,
  item_color: bundledPotVariant.Color,
  item_availability: bundledPotVariant.available,
});

export const parseProductCustomMetricsFromTags = (tags: string[]) => {
  const careLevels = tags.filter((t) => CareLevelTags.includes(t));
  const waterNeeds = tags.filter((t) => WaterNeedTags.includes(t));
  const humidityLevels = tags.filter((t) => HumidityLevelTags.includes(t));
  const lightLevels = tags.filter((t) => LightLevelTags.includes(t));
  const petFriendliness = tags.filter((t) => PetFriendlinessTags.includes(t));
  const zoneTags = tags
    .filter((t) => /^zone-\d+$/.test(t))
    .sort(zoneComparator);

  return {
    item_care_level: careLevels.join(',') || null,
    item_water_need: waterNeeds.join(',') || null,
    item_humidity_level: humidityLevels.join(',') || null,
    item_light_level: lightLevels.join(',') || null,
    item_pet_friendliness: petFriendliness.join(',') || null,
    item_hardiness_zone: zoneTags.length >= 2 ? `${zoneTags[0]} | ${zoneTags[zoneTags.length - 1]}` : null,
  };
};

export const parseProductCustomMetrics = (product: ProductSubsetForAnalytics,
  variant: PottedProductVariant | VariantSubsetForAnalytics,
  bundledPotVariant?: BundledPotProductAndVariantInfo) => {
  const { tags } = product;
  const tagBasedMetrics = parseProductCustomMetricsFromTags(tags);

  return {
    ...tagBasedMetrics,
    item_availability: variant.available,
    item_size: getSizeMetric(product, variant) || null,
    item_color: getColorMetric(product, variant, bundledPotVariant) || null,
  };
};

export const getProductGtagData = (
  mainProductAndVariantInfo: MainProductAndVariantInfo,
  bundledPotVariant?: BundledPotProductAndVariantInfo,
) => {
  const { product: mainProduct, variant, metadata } = mainProductAndVariantInfo;
  const pottedProductVariant = variant as PottedProductVariant;
  const productVariant = variant as Variant;

  const productTags = mainProduct.tags;
  let productQuantity = 1;
  let itemsInPack = 1;
  let collection = null;
  let productIndex = 1;

  if (metadata) {
    productQuantity = metadata.quantity || productQuantity;
    itemsInPack = metadata.itemsInPack || itemsInPack;
    collection = metadata.collection;
    productIndex = metadata.productIndex === undefined ? 1 : (metadata.productIndex + 1);
  }

  const productOriginalPrice = productVariant.compare_at_price || productVariant.price;
  const productDiscount = productVariant.compare_at_price
    ? (productVariant.compare_at_price - productVariant.price) : 0;

  const productCategoryData = parseTagsForProductCategories(productTags);
  const customMetrics = parseProductCustomMetrics(mainProduct, variant);

  const mainProductData = {
    item_id: `${mainProduct.id}`,
    item_name: mainProduct.title,
    affiliation: 'Lively Root - Online Shop',
    coupon: null as string | null,
    currency: 'USD',
    discount: productDiscount / 100,
    index: productIndex,
    item_brand: mainProduct.vendor,
    item_list_id: collection?.id ? `${collection.id}` : null,
    item_list_name: collection?.title || null,
    item_variant: pottedProductVariant.variant_name || productVariant.title,
    price: productOriginalPrice / 100,
    quantity: productQuantity,
    ...productCategoryData,
    ...customMetrics,
  };

  let bundledPotData = null;

  if (bundledPotVariant) {
    const potOriginalPrice = bundledPotVariant.compare_at_price || bundledPotVariant.price;
    const potDiscount = bundledPotVariant.compare_at_price
      ? (bundledPotVariant.compare_at_price - bundledPotVariant.price) : 0;
    const potQuantity = productQuantity * itemsInPack;

    const potCategoryData = parseTagsForProductCategories(bundledPotVariant.tags);

    bundledPotData = {
      item_id: bundledPotVariant.id,
      item_name: bundledPotVariant.product_name,
      affiliation: 'Lively Root - Online Shop',
      coupon: null as string | null,
      currency: 'USD',
      discount: potDiscount / 100,
      index: 2,
      item_brand: bundledPotVariant.product_vendor,
      item_list_id: null as string | null,
      item_list_name: null as string | null,
      item_variant: bundledPotVariant.variant_name,
      price: potOriginalPrice / 100,
      quantity: potQuantity,
      ...potCategoryData,
      ...parsePotCustomMetrics(bundledPotVariant),
    };
  }

  return [mainProductData, ...(bundledPotData ? [bundledPotData] : [])];
};

export const getCartOrLineItemGtagData = (item: CartItem | LineItem, itemQuantity: number) => {
  const { data: { product, variant } } = item;

  const originalPrice = variant.compare_at_price || variant.price;
  const lineItem = item as LineItem;
  let lineItemDiscount = 0;

  const variantLevelDiscount = variant.compare_at_price
    ? (variant.compare_at_price - variant.price) : 0;

  if (lineItem.data.lineItem) {
    // Price read from line item doesn't include discounts set at product/variant level
    // originalPrice = lineItem.data.lineItem.originalPrice;
    lineItemDiscount = lineItem.data.lineItem.discountValue / lineItem.data.quantity;
  }

  const totalDiscount = variantLevelDiscount + lineItemDiscount;

  const categoryData = parseTagsForProductCategories(product.tags);
  const customMetrics = parseProductCustomMetrics(product, variant);

  return {
    item_id: `${product.id}`,
    item_name: `${product.title}`,
    affiliation: 'Lively Root - Online Shop',
    coupon: null as string | null,
    currency: 'USD',
    discount: totalDiscount / 100,
    item_brand: product.vendor,
    item_list_id: null as string | null,
    item_list_name: null as string | null,
    item_variant: variant.name,
    price: originalPrice / 100,
    quantity: itemQuantity,
    ...categoryData,
    ...customMetrics,
  };
};
